/**
 * Frontend-Service: Premium-Support-Chat Admin-REST-Wrapper
 *
 * Backend-Endpoints (siehe backend/src/routes/support/chatAdmin.ts):
 *   GET  /admin/support/badge                       -- Anzahl Kunden mit ungelesenem
 *   GET  /admin/support/conversations               -- Inbox-Liste
 *   GET  /admin/support/conversations/:id           -- Detail
 *   GET  /admin/support/conversations/:id/messages  -- Nachrichten
 *   POST /admin/support/conversations/:id/messages  -- Als Admin antworten
 *   POST /admin/support/conversations/:id/read      -- Als Admin markieren
 */

import apiClient from '../api/apiClient';
import type { ChatMessage, ConversationStatus, ConversationCategory } from './chatService';

export type AdminChatMessage = ChatMessage;

export interface AdminConversationListItem {
  id: string;
  status: ConversationStatus;
  category: ConversationCategory | null;
  subject: string | null;
  unreadCountAdmin: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageSenderType: 'user' | 'admin' | 'system' | null;
  createdAt: string;
  userId: string;
  userDisplayName: string;
  userUsername: string | null;
  userEmail: string | null;
  /** Vom Admin gesetzter Avatar; null = Initialen-Fallback im UI */
  userAvatarUrl: string | null;
  planSlugAtCreation: string | null;
}

export interface AdminConversationDetail extends AdminConversationListItem {
  unreadCountUser: number;
  isReadOnly: boolean;
  updatedAt: string;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

export async function fetchAdminBadge(): Promise<number> {
  const res = await apiClient.get<SuccessEnvelope<{ unreadConversationCount: number }>>('/admin/support/badge');
  return res.data.data.unreadConversationCount;
}

export async function fetchAdminConversations(): Promise<AdminConversationListItem[]> {
  const res = await apiClient.get<SuccessEnvelope<{ conversations: AdminConversationListItem[] }>>(
    '/admin/support/conversations'
  );
  return res.data.data.conversations;
}

export async function fetchAdminConversationDetail(id: string): Promise<AdminConversationDetail> {
  const res = await apiClient.get<SuccessEnvelope<{ conversation: AdminConversationDetail }>>(
    `/admin/support/conversations/${id}`
  );
  return res.data.data.conversation;
}

export async function fetchAdminMessages(
  conversationId: string,
  options: { before?: string; limit?: number } = {}
): Promise<{ messages: AdminChatMessage[]; conversation: AdminConversationDetail }> {
  const params = new URLSearchParams();
  if (options.before) params.set('before', options.before);
  if (options.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const url = `/admin/support/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`;
  const res = await apiClient.get<SuccessEnvelope<{ messages: AdminChatMessage[]; conversation: AdminConversationDetail }>>(url);
  return res.data.data;
}

export async function sendAdminMessage(
  conversationId: string,
  content: string,
  options: { isInternalNote?: boolean; replyToMessageId?: string | null } = {}
): Promise<AdminChatMessage> {
  const res = await apiClient.post<SuccessEnvelope<{ message: AdminChatMessage }>>(
    `/admin/support/conversations/${conversationId}/messages`,
    {
      content,
      isInternalNote: options.isInternalNote === true,
      ...(options.replyToMessageId ? { replyToMessageId: options.replyToMessageId } : {})
    }
  );
  return res.data.data.message;
}

/**
 * Admin-Pendant zu sendMessageWithImages: schickt bis zu 3 Bilder + optional
 * Text + optional internal-note-Flag.
 */
export async function sendAdminMessageWithImages(
  conversationId: string,
  text: string,
  images: File[],
  options: { isInternalNote?: boolean; captions?: (string | null)[]; replyToMessageId?: string | null } = {}
): Promise<AdminChatMessage> {
  if (images.length === 0 && text.trim().length === 0) {
    throw new Error('Bitte Text oder mindestens ein Bild hinzufügen.');
  }
  if (images.length > 3) {
    throw new Error('Maximal 3 Bilder pro Nachricht.');
  }

  const captions = options.captions ?? [];
  const formData = new FormData();
  formData.append('text', text);
  formData.append('isInternalNote', options.isInternalNote === true ? 'true' : 'false');
  if (options.replyToMessageId) {
    formData.append('replyToMessageId', options.replyToMessageId);
  }
  for (let i = 0; i < images.length; i++) {
    formData.append('images', images[i]!, images[i]!.name);
    formData.append('captions', captions[i] ?? '');
  }

  // Content-Type unsetten — siehe Begründung in chatService.sendMessageWithImages
  const res = await apiClient.post<SuccessEnvelope<{ message: AdminChatMessage }>>(
    `/admin/support/conversations/${conversationId}/messages/with-images`,
    formData,
    { headers: { 'Content-Type': undefined } as any }
  );
  return res.data.data.message;
}

export async function markAdminConversationRead(conversationId: string): Promise<void> {
  await apiClient.post(`/admin/support/conversations/${conversationId}/read`, {});
}
